// ============================================
// Modelos de Datos
// ============================================
export interface AuditLogDto {
  id: string;
  timestamp: string;
  
  // Usuario
  userId?: string;
  username?: string;
  userRole?: string;
  
  // Cliente OAuth
  clientId?: string;
  clientType?: string;
  
  // Acción
  action: string;
  module: string;
  entityType?: string;
  entityId?: string;
  
  // Detalles
  oldValues?: Record<string, any>;
  newValues?: Record<string, any>;
  description?: string;
  
  // Contexto HTTP
  ipAddress?: string;
  userAgent?: string;
  requestMethod?: string;
  requestPath?: string;
  
  // Clasificación
  severity: AuditSeverity;
  isSuccess: boolean;
}

export interface PaginationDto {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface AuditLogPagedResponse {
  data: AuditLogDto[];
  pagination: PaginationDto;
}

export interface TimelineEventDto {
  id: string;
  timestamp: string;
  user?: string;
  action: string;
  description?: string;
  oldValues?: Record<string, any>;
  newValues?: Record<string, any>;
}

export interface EntityTimelineDto {
  entityType: string;
  entityId: string;
  events: TimelineEventDto[];
}

export interface AuditStatsDto {
  period: {
    from: string;
    to: string;
  };
  totalEvents: number;
  byModule: Record<string, number>;
  byAction: Record<string, number>;
  bySeverity: Record<string, number>;
  mostActiveUsers: TopUserDto[];
  failureRate: number;
}

export interface TopUserDto {
  userId?: string;
  username?: string;
  eventCount: number;
}

// ============================================
// Enums y Tipos
// ============================================
export type AuditSeverity = 'Info' | 'Warning' | 'Error' | 'Critical';

export type AuditAction = 
  | 'Created' 
  | 'Updated' 
  | 'Deleted' 
  | 'Login' 
  | 'Logout' 
  | 'LoginFailed'
  | 'TokenRefreshed'
  | 'Printed'
  | 'Exported'
  | string; // Permite acciones personalizadas

export type AuditModule = 
  | 'Authentication'
  | 'WorkOrders'
  | 'Customers'
  | 'Items'
  | 'Farmers'
  | 'Production'
  | 'Printing'
  | 'Auditing'
  | string; // Permite módulos personalizados

export type RequestMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type ExportFormat = 'csv' | 'json';

// ============================================
// Filtros
// ============================================
export interface AuditLogFilters {
  userId?: string;
  clientId?: string;
  module?: string;
  action?: string;
  entityType?: string;
  entityId?: string;
  fromDate?: string;
  toDate?: string;
  severity?: AuditSeverity;
  isSuccess?: boolean;
  ipAddress?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface AuditStatsFilters {
  fromDate?: string;
  toDate?: string;
}

export interface AuditExportFilters {
  format?: ExportFormat;
  userId?: string;
  clientId?: string;
  module?: string;
  action?: string;
  fromDate?: string;
  toDate?: string;
}

// ============================================
// Utilidades
// ============================================
export const SEVERITY_COLORS: Record<AuditSeverity, string> = {
  Info: 'blue',
  Warning: 'yellow',
  Error: 'orange',
  Critical: 'red',
};

export const SEVERITY_LABELS: Record<AuditSeverity, string> = {
  Info: 'Información',
  Warning: 'Advertencia',
  Error: 'Error',
  Critical: 'Crítico',
};

export const ACTION_LABELS: Record<string, string> = {
  Created: 'Creado',
  Updated: 'Actualizado',
  Deleted: 'Eliminado',
  Login: 'Inicio de sesión',
  Logout: 'Cierre de sesión',
  LoginFailed: 'Login fallido',
  TokenRefreshed: 'Token renovado',
  Printed: 'Impreso',
  Exported: 'Exportado',
};

export const MODULE_LABELS: Record<string, string> = {
  Authentication: 'Autenticación',
  WorkOrders: 'Órdenes de Trabajo',
  Customers: 'Clientes',
  Items: 'Artículos',
  Farmers: 'Agricultores',
  Production: 'Producción',
  Printing: 'Impresión',
  Auditing: 'Auditoría',
};
